import { motion } from "framer-motion";
import { Reveal, StatusBadge, GoldButton, EASE } from "./shared";
import { Lightbulb } from "lucide-react";

export function YeidaSectorPanel({ data, onEnquire }) {
  return (
    <div data-testid={`yeida-panel-${data.id}`}>
      <div className="grid lg:grid-cols-5 gap-6 mb-10">
        <Reveal className="lg:col-span-2" delay={0.05}>
          <div className="relative h-[260px] lg:h-full overflow-hidden rounded-[2px] border border-[#D4AF37]/20">
            <img src={data.image} alt={`${data.sector} - ${data.tag}`} className="w-full h-full object-cover img-cinematic" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/10" />
            <div className="absolute bottom-0 inset-x-0 p-5 flex items-end justify-between">
              <div>
                <p className="font-serif-lux text-2xl text-[#F5F5F0]">{data.sector}</p>
                <p className="text-[11px] text-[#A1A1AA] mt-1">{data.tag}</p>
              </div>
              <StatusBadge status={data.status} />
            </div>
            <p className="absolute top-3 left-3 text-[9px] uppercase tracking-[0.2em] text-[#71717A] bg-black/50 px-2 py-1 rounded-[2px]">{data.imageCaption}</p>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-3" delay={0.1}>
          <div className="card-lux p-6 lg:p-7 rounded-[2px] h-full flex flex-col">
            <p className="font-serif-lux text-xl text-[#F5F5F0] mb-4">{data.headline}</p>
            <dl className="divide-y divide-[#D4AF37]/10 mb-5">
              {data.facts.map(([k, v]) => (
                <div key={k} className="grid sm:grid-cols-[130px_1fr] gap-1 sm:gap-5 py-2.5">
                  <dt className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/80 pt-0.5">{k}</dt>
                  <dd className="text-[12px] text-[#A1A1AA] leading-relaxed">{v}</dd>
                </div>
              ))}
            </dl>
            <GoldButton data-testid={`yeida-panel-${data.id}-enquire-btn`} onClick={() => onEnquire(`${data.sector} - ${data.tag}`)} className="mt-auto w-fit">
              Ask About {data.sector}
            </GoldButton>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div className="card-lux p-6 lg:p-7 rounded-[2px] mb-6" data-testid={`yeida-panel-${data.id}-plot-details`}>
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Plot Details</p>
            <p className="text-[12px] text-[#A1A1AA]">{data.plotSummary} &middot; <span className="text-[#F3E5AB]">{data.rateSummary}</span></p>
          </div>
          <dl className="divide-y divide-[#D4AF37]/10">
            {data.plotDetails.map(([k, v]) => (
              <div key={k} className="grid sm:grid-cols-[130px_1fr] gap-1 sm:gap-5 py-2.5">
                <dt className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/80 pt-0.5">{k}</dt>
                <dd className="text-[12px] text-[#A1A1AA] leading-relaxed">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </Reveal>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: EASE }}
        className="flex gap-3 p-5 border border-[#D4AF37]/20 bg-[#D4AF37]/[0.04] rounded-[2px] mb-6"
        data-testid={`yeida-panel-${data.id}-opportunity`}
      >
        <Lightbulb size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1.5">Hidden Opportunity</p>
          <p className="text-[13px] text-[#A1A1AA] leading-relaxed">{data.hiddenOpportunity}</p>
        </div>
      </motion.div>

      <p className="text-[11px] text-[#52525b] leading-relaxed" data-testid={`yeida-panel-${data.id}-disclaimer`}>
        {data.disclaimer}
      </p>
    </div>
  );
}
